import { useCallback, useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import { Menu } from 'antd';
import type { MenuProps } from 'antd';
import { MenuFoldOutlined, MenuUnfoldOutlined } from '@ant-design/icons';
import useAuth from '../../Hooks/useAuth';
import {
    Divider,
    Input,
    Sider,
    Skeleton,
    Tooltip,
} from '@servahealth/serva-ui-kit'
import MenuItem from '../../Interfaces/Responses/Dashboard/MenuItem';
import Report from '../../Interfaces/Responses/Dashboard/Report';
import SubMenuItem from '../../Interfaces/Responses/Dashboard/SubMenuItem';
import { buildReportGroupsQueryKey, getReportsForUserByCurrentToken } from '../Shared/ReportMenuQuery';

const REPORT_MENU_WIDTH = 300;
const REPORT_MENU_RAIL_WIDTH = 52;

interface ReportSideNavProps {
    collapsed: boolean;
    setCollapsed: (collapsed: boolean) => void;
}

const ReportSideNav = ({
        collapsed,
        setCollapsed,
    }: ReportSideNavProps) => {
    const location = useLocation();
    const { user } = useAuth();
    const nav = useNavigate();
    const [searchText, setSearchText] = useState('');
    const [menuOpenKeys, setMenuOpenKeys] = useState(['']);
    const [reportMenuItems, setReportMenuItems] = useState<MenuItem[]>([]);
    const [filteredReportMenuItems, setFilteredReportMenuItems] = useState<MenuItem[]>([]);

    const { isLoading: isLoadReportGroupsLoading, isRefetching: isLoadReportGroupsRefetching } = useQuery(
        buildReportGroupsQueryKey(user?.runningUser.currentToken),
        () => getReportsForUserByCurrentToken(user?.runningUser.currentToken ?? ''),
        {
            enabled: !!user?.runningUser.currentToken,
            retry: false,
            onError: (err: any) => {
                console.error('GET_REPORTS_FOR_USER_BY_CURRENT_TOKEN: [Get Report Groups] Internal Error: ', err);
            },
            onSuccess: (response: Report[]) => {
                const menuItems: MenuItem[] = [];

                response?.forEach((report) => {

                    const grouping = report.grouping;

                    const existingMenuItem = menuItems.find(
                        (eachReport) => eachReport.label === grouping
                    );

                    const newSubmenuItem: SubMenuItem = {
                        label: report.display,
                        onClick: () => nav(`/home/${report.value}`),
                        key: `/home/${report.value}` // the selectedKeys from the menu will be based on URL path
                    };

                    if (existingMenuItem) {
                        existingMenuItem.subItems.push(newSubmenuItem);
                    } else {
                        const newMenuItem: MenuItem = {
                            label: report.grouping,
                            subItems: [newSubmenuItem],
                        };
                        menuItems.push(newMenuItem);
                    }
                });

                setReportMenuItems(menuItems);
                setFilteredReportMenuItems(menuItems);
            },
            refetchOnWindowFocus: false,
            refetchOnReconnect: false,
        }
    );

    const handleSearchTextChange = (aValue: any) => {
        setSearchText(aValue.target.value);
    };

    const handleOpenKeyChange = (openKeys: string[]) => {
        let currentOpenKey = openKeys.find((key) => menuOpenKeys.indexOf(key) === -1);
    
        // open
        if (currentOpenKey !== undefined) {
            setMenuOpenKeys(menuOpenKeys.concat(currentOpenKey));
        }
        // close
        else {
            setMenuOpenKeys(openKeys);
        }
    }

    const doSearch = useCallback((sValue: string) => {
        let searchTextL = searchText;

        // --DEBUG
        // console.log('doSearch: sValue: ' + sValue);
        // console.log('doSearch: searchText: ' + searchText);
        // --ENDDEBUG

        if (sValue === '') {
            setFilteredReportMenuItems(reportMenuItems);
            setMenuOpenKeys([]);
            return;
        }
        else if (searchText === '') {
            setFilteredReportMenuItems(reportMenuItems);
            setMenuOpenKeys([]);
            return;
        }
        else {
            let filteredMenuItems: MenuItem[] = [];
            let filteredDefaultMenuOpenKeys: string[] = [];

            reportMenuItems.forEach((rmi) => {
                if (rmi.label.toLowerCase().includes(searchTextL.toLocaleLowerCase())) {
                    filteredMenuItems.push(rmi);
                }
                else {
                    let filteredSubMenuItems: SubMenuItem[] = [];

                    rmi.subItems.forEach((rsmi) => {
                        if (rsmi.label.toLowerCase().includes(searchTextL.toLocaleLowerCase())) {
                            let filteredSubMenuItem: SubMenuItem = { label: '', onClick: () => {}, key: '' };

                            filteredSubMenuItem.label = rsmi.label;
                            filteredSubMenuItem.onClick = rsmi.onClick;
                            filteredSubMenuItem.key = rsmi.key;

                            filteredSubMenuItems.push(filteredSubMenuItem);

                            if (filteredDefaultMenuOpenKeys.indexOf(rmi.label) === -1)
                            {
                                filteredDefaultMenuOpenKeys.push(rmi.label);
                            }
                        }
                    });

                    if (filteredSubMenuItems.length > 0) {
                        let filteredMenuItem: MenuItem = { label: '', subItems: [] };

                        filteredMenuItem.label = rmi.label;
                        filteredMenuItem.subItems = filteredSubMenuItems;

                        filteredMenuItems.push(filteredMenuItem);
                    }
                }
            });

            if (filteredDefaultMenuOpenKeys.length > 0)
            {
                setMenuOpenKeys(filteredDefaultMenuOpenKeys);
            }
            else
            {
                setMenuOpenKeys([]);
            }

            setFilteredReportMenuItems(filteredMenuItems);
        }
    }, [reportMenuItems, searchText]);

    useEffect(() => {
        const makeSearchCall = () => {
            try {
                doSearch(searchText);
            } catch (e) {

            }
        }

        const triggerCall = setTimeout(() => {
            makeSearchCall();
        }, 500)

        return () => clearTimeout(triggerCall)
    }, [searchText, doSearch])

    const sideMenuItems: MenuProps['items'] = filteredReportMenuItems.map((group) => ({
        key: group.label,
        label: (
            <Tooltip title={group.label} placement='right'>
                <span>{group.label}</span>
            </Tooltip>
        ),
        children: group.subItems.map((subItem) => ({
            key: subItem.key,
            label: (
                <Tooltip title={subItem.label} placement='right'>
                    <span>{subItem.label}</span>
                </Tooltip>
            ),
            onClick: subItem.onClick,
        })),
    }));

    return (
        <Sider
            className={`serva2025theme-side-menu ${collapsed ? 'serva2025theme-side-menu-collapsed' : 'serva2025theme-side-menu-expanded'}`}
            style={{
                bottom: 0,
                display: 'flex',
                flexDirection: 'column',
                fontSize: 30,
                height: 'calc(100vh - var(--retainbi-header-height) - 40px)',
                left: 0,
                overflowY: 'hidden',
                overflowX: 'hidden',
                paddingTop: 10,
                transition: 'width 0.2s ease',
            }}
            theme="dark"
            width={collapsed ? REPORT_MENU_RAIL_WIDTH : REPORT_MENU_WIDTH}
        >
            {
                collapsed
                ?
                    (
                        <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 4 }}>
                            <Tooltip title='Expand report menu' placement='right'>
                                <button
                                    type='button'
                                    aria-label='Expand report menu'
                                    onClick={() => setCollapsed(false)}
                                    className='report-menu-toggle-button'
                                >
                                    <MenuUnfoldOutlined />
                                </button>
                            </Tooltip>
                        </div>
                    )
                :
                    (
                        <>
                            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 6 }}>
                                <Input
                                    allowClear={true}
                                    search={true}
                                    style={{ width: '78%' }}
                                    value={searchText}
                                    onChange={handleSearchTextChange}
                                    onSearch={doSearch}
                                ></Input>
                                <Tooltip title='Collapse report menu' placement='right'>
                                    <button
                                        type='button'
                                        aria-label='Collapse report menu'
                                        onClick={() => setCollapsed(true)}
                                        className='report-menu-toggle-button'
                                    >
                                        <MenuFoldOutlined />
                                    </button>
                                </Tooltip>
                            </div>
                            <Divider style={{ padding: 0, margin: 8 }} />
                            {
                                (isLoadReportGroupsLoading || isLoadReportGroupsRefetching)
                                ?
                                    (
                                        <Skeleton active />
                                    )
                                :
                                    (
                                        <div style={{ flex: '1 1 auto', minHeight: 0, overflowY: 'auto', overflowX: 'hidden' }}>
                                            <Menu
                                                mode='inline'
                                                selectedKeys={[location.pathname]}
                                                style={{ width: '100%' }}
                                                theme='light'
                                                openKeys={menuOpenKeys}
                                                onOpenChange={handleOpenKeyChange}
                                                items={sideMenuItems}
                                            />
                                        </div>
                                    )
                            }
                            <Divider style={{ padding: 0, margin: 4 }} />
                            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 6, height: 10 }}>
                                &nbsp;
                            </div>
                        </>
                    )
            }
        </Sider>
    )
}

export default ReportSideNav;